import type { Feature, FeatureCollection, Geometry, GeoJsonProperties } from 'geojson'

/**
 * Utility for mapping entities to GeoJSON FeatureCollections.
 *
 * Provides a reusable pattern for converting domain entities
 * into MapBox-compatible GeoJSON structures.
 */
export class FeatureMapper {
  /**
   * Map an array of entities to a GeoJSON FeatureCollection.
   *
   * @template T - The entity type
   * @param entities - Array of entities to map
   * @param mapperFn - Function that maps a single entity to a Feature
   * @returns GeoJSON FeatureCollection
   *
   * @example
   * const features = FeatureMapper.toFeatureCollection(
   *   stations,
   *   (station) => ({
   *     type: 'Feature',
   *     geometry: { type: 'Point', coordinates: station.getLngLat() },
   *     properties: { id: station.id, name: station.name }
   *   })
   * )
   */
  static toFeatureCollection<T>(
    entities: T[],
    mapperFn: (entity: T) => Feature<Geometry, GeoJsonProperties>,
  ): FeatureCollection<Geometry, GeoJsonProperties> {
    return {
      type: 'FeatureCollection',
      features: entities.map(mapperFn),
    }
  }

  /**
   * Create an empty FeatureCollection.
   *
   * @returns Empty GeoJSON FeatureCollection
   */
  static empty(): FeatureCollection<Geometry, GeoJsonProperties> {
    return {
      type: 'FeatureCollection',
      features: [],
    }
  }
}
